import { HomePaths } from '@/modules/Paths';
import clsx from 'clsx';
import Image from 'next/image';
import Link from 'next/link';
import { useMemo, useState } from 'react';
import { SectionTitle } from '../SectionTitle';
import { useTranslate } from 'app/[locale]/hooks/useTranslate';
import analyticsInstance from 'libs/rtzr-commons/modules/marketing/Analytics';
import { useAttentionTracker } from 'app/[locale]/hooks/useAttentionTracker';

type IntegrationTab = 'all' | 'calendar' | 'messenger' | 'crm' | 'etc';

export const ToolsSection = (): JSX.Element => {
  const { t } = useTranslate();
  const { ref } = useAttentionTracker({ sectionName: 'main_integrations' });

  return (
    <section className="space-y-8 max-w-homepage px-4 md:px-10" ref={ref}>
      <SectionTitle
        title={t.home.MAIN_INTEGRATION_TITLE}
        description={t.home.MAIN_INTEGRATION_DESC}
        href={HomePaths.IntegrationsIndex}
      />
      <ToolsTab />
    </section>
  );
};

const ToolsTab = (): JSX.Element => {
  const [tab, setTab] = useState<IntegrationTab>('all');
  const [enableCalendar, enableMessenger, enableCrm, enableEtc]: boolean[] = useMemo(() => {
    if (tab === 'all') return [true, true, true, true];
    return [tab === 'calendar', tab === 'messenger', tab === 'crm', tab === 'etc'];
  }, [tab]);

  return (
    <div className="flex flex-col items-center gap-10 md:gap-20">
      <div className="flex gap-2 flex-wrap items-center justify-center">
        <Tab value="All" isSelected={tab === 'all'} onClick={() => setTab('all')} />
        <Tab value="Calendar" isSelected={tab === 'calendar'} onClick={() => setTab('calendar')} />
        <Tab
          value="Messenger"
          isSelected={tab === 'messenger'}
          onClick={() => setTab('messenger')}
        />
        <Tab value="CRM" isSelected={tab === 'crm'} onClick={() => setTab('crm')} />
        <Tab value="ETC" isSelected={tab === 'etc'} onClick={() => setTab('etc')} />
      </div>
      <div className="flex flex-wrap md:gap-12 md:px-5 lg:px-5 gap-3.5 px-0.5 items-center justify-center w-full max-w-[700px]">
        {enableCalendar && (
          <>
            <ToolItem
              imgSrc="/callabo/integration/google_calendar.svg"
              name="Calendar"
              path={HomePaths.IntegrationsGoogleCalendar}
            />
            <ToolItem
              imgSrc="/callabo/integration/outlook_calendar.svg"
              name="MS Outlook"
              path={HomePaths.IntegrationsOutlookCalendar}
            />
          </>
        )}
        {enableCrm && (
          <>
            <ToolItem
              imgSrc="/callabo/integration/salesforce.svg"
              name="Salesforce"
              path={HomePaths.IntegrationsSalesforce}
            />
            <ToolItem
              imgSrc="/callabo/integration/pipedrive.svg"
              name="Pipedrive"
              path={HomePaths.IntegrationsPipedrive}
            />
            <ToolItem
              imgSrc="/callabo/integration/hubspot.svg"
              name="HubSpot"
              path={HomePaths.IntegrationsIndex}
            />
          </>
        )}
        {enableMessenger && (
          <>
            <ToolItem
              imgSrc="/callabo/homepage/slack_new.svg"
              name="Slack"
              path={HomePaths.IntegrationsSlack}
            />
            <ToolItem
              imgSrc="/callabo/integration/teams.svg"
              name="MS Teams"
              path={HomePaths.IntegrationsTeams}
            />
          </>
        )}
        {enableEtc && (
          <>
            <ToolItem
              imgSrc="/callabo/integration/zapier.svg"
              name="Zapier"
              path={HomePaths.IntegrationsZapier}
            />
            <ToolItem
              imgSrc="/callabo/integration/notion.svg"
              name="Notion"
              path={HomePaths.IntegrationsNotion}
            />
            <ToolItem
              imgSrc="/callabo/integration/confluence.svg"
              name="Confluence"
              path={HomePaths.IntegrationsConfluence}
            />
            <ToolItem
              imgSrc="/callabo/integration/linear.svg"
              name="Linear"
              path={HomePaths.IntegrationsLinear}
            />
          </>
        )}
      </div>
    </div>
  );
};

export const Tab = ({
  value,
  isSelected,
  onClick,
}: {
  value: string;
  isSelected: boolean;
  onClick: () => void;
}): JSX.Element => {
  return (
    <li
      className={clsx(
        'px-5 py-3.5 md:px-6 md:py-5 md:text-lg text-black-light rounded-[4px]  border border-gray-soft cursor-pointer list-none',
        isSelected ? 'bg-blue-pale font-semibold' : 'bg-white'
      )}
      onClick={onClick}
      onMouseDown={() => {
        analyticsInstance.logEvent('click_integration_tab', {
          tab: value,
        });
      }}
    >
      {value}
    </li>
  );
};

const ToolItem = ({ imgSrc, name, path }: { imgSrc: string; name: string; path: string }) => {
  const [isHover, setIsHover] = useState(false);

  return (
    <Link
      href={path}
      target="_blank"
      className={clsx(
        'relative w-[80px] md:w-[128px] h-24 md:h-[120px] overflow-visible bg-white md:mb-4',
        isHover ? 'z-50' : 'z-0'
      )}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      passHref
    >
      <button
        className={clsx(
          'absolute top-0 left-0 flex flex-col md:pt-5  items-center gap-4 w-full transition-transform duration-100 origin-top bg-white',
          isHover ? 'z-50 md:h-[215px] md:shadow-pale-xs md:rounded-xl' : 'h-[120px] z-0'
        )}
        onMouseDown={() => {
          analyticsInstance.logEvent('click_go_to_page', {
            page_url: path,
            tool: name,
          });
        }}
      >
        <Image
          src={imgSrc}
          alt={name}
          width={80}
          height={80}
          className="flex-shrink-0 w-10 h-10 md:w-20 md:h-20"
        />
        <span className="text-xs md:text-base">{name}</span>
        {isHover && (
          <div className="absolute gap-1.5 text-gray-soft w-[70px] items-center px-1 bottom-[50px] hidden md:flex">
            <div className="flex-1 h-px bg-gray-soft" />
            <span className="text-xs">with</span>
            <div className="flex-1 h-px bg-gray-soft" />
          </div>
        )}
        {isHover && (
          <Image
            className="absolute h-[26px] bottom-5 hidden md:block"
            src="/callabo/homepage/logo_with_margin.svg"
            width={70}
            height={26}
            alt="callabo logo"
          />
        )}
      </button>
    </Link>
  );
};
